/**
 * Transaction process graph for bookings:
 *   - default-booking
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Marketplace API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const transitions = {
  // When a customer makes a booking to a listing, a transaction is
  // created with the initial request-payment transition.
  // At this transition a PaymentIntent is created by Marketplace API.
  // After this transition, the actual payment must be made on client-side directly to Stripe.
  REQUEST_PAYMENT: 'transition/request-payment',

  // A customer can also initiate a transaction with an inquiry, and
  // then transition that with a request.
  INQUIRE: 'transition/inquire',
  REQUEST_PAYMENT_AFTER_INQUIRY: 'transition/request-payment-after-inquiry',

  // Provider can make an offer from the inquiry state.
  // Customer and provider can counter-offer back and forth.
  // Either party can accept to move to offer-accepted.
  MAKE_OFFER: 'transition/make-offer',
  CUSTOMER_COUNTER_OFFER: 'transition/customer-counter-offer',
  PROVIDER_COUNTER_OFFER: 'transition/provider-counter-offer',
  CUSTOMER_ACCEPT_OFFER: 'transition/customer-accept-offer',
  PROVIDER_ACCEPT_OFFER: 'transition/provider-accept-offer',

  // Before payment, the dates are held by a pending booking. To release them, the transaction
  // can be canceled before it gets to the payment.
  // While negotiating (inquiry and the offer states) there are four ways out: the customer, the
  // provider or the operator cancels, or the backend cancels automatically when nobody has acted
  // for 7 days. Once the offer is accepted, the customer can still cancel at any time before
  // paying (the client asked for this specifically); the provider and the operator cannot, and
  // for everyone the backend cancels automatically after 3 days without a payment.
  // Keep these in sync with "Cancelling before payment" in
  // ext/transaction-processes/default-booking/process.edn
  CUSTOMER_CANCEL_FROM_INQUIRY: 'transition/customer-cancel-from-inquiry',
  PROVIDER_CANCEL_FROM_INQUIRY: 'transition/provider-cancel-from-inquiry',
  OPERATOR_CANCEL_FROM_INQUIRY: 'transition/operator-cancel-from-inquiry',
  AUTO_CANCEL_FROM_INQUIRY: 'transition/auto-cancel-from-inquiry',
  CUSTOMER_CANCEL_FROM_OFFER_PENDING: 'transition/customer-cancel-from-offer-pending',
  PROVIDER_CANCEL_FROM_OFFER_PENDING: 'transition/provider-cancel-from-offer-pending',
  OPERATOR_CANCEL_FROM_OFFER_PENDING: 'transition/operator-cancel-from-offer-pending',
  AUTO_CANCEL_FROM_OFFER_PENDING: 'transition/auto-cancel-from-offer-pending',
  CUSTOMER_CANCEL_FROM_CUSTOMER_OFFER_PENDING:
    'transition/customer-cancel-from-customer-offer-pending',
  PROVIDER_CANCEL_FROM_CUSTOMER_OFFER_PENDING:
    'transition/provider-cancel-from-customer-offer-pending',
  OPERATOR_CANCEL_FROM_CUSTOMER_OFFER_PENDING:
    'transition/operator-cancel-from-customer-offer-pending',
  AUTO_CANCEL_FROM_CUSTOMER_OFFER_PENDING: 'transition/auto-cancel-from-customer-offer-pending',
  CUSTOMER_CANCEL_FROM_OFFER_ACCEPTED: 'transition/customer-cancel-from-offer-accepted',
  AUTO_CANCEL_FROM_OFFER_ACCEPTED: 'transition/auto-cancel-from-offer-accepted',

  // Stripe SDK might need to ask 3D security from customer, in a separate front-end step.
  // Therefore we need to make another transition to Marketplace API,
  // to tell that the payment is confirmed.
  CONFIRM_PAYMENT: 'transition/confirm-payment',

  // If the payment is not confirmed in the time limit set in transaction process (by default 15min)
  // the transaction will expire automatically.
  EXPIRE_PAYMENT: 'transition/expire-payment',

  // When the provider accepts or declines a transaction from the
  // SalePage, it is transitioned with the accept or decline transition.
  ACCEPT: 'transition/accept',
  DECLINE: 'transition/decline',

  // The operator can accept or decline the offer on behalf of the provider
  OPERATOR_ACCEPT: 'transition/operator-accept',
  OPERATOR_DECLINE: 'transition/operator-decline',

  // The backend automatically expire the transaction.
  EXPIRE: 'transition/expire',

  // Admin can also cancel the transition.
  CANCEL: 'transition/cancel',

  // The backend will mark the transaction completed.
  COMPLETE: 'transition/complete',
  OPERATOR_COMPLETE: 'transition/operator-complete',

  // Reviews are given through transaction transitions. Review 1 can be
  // by provider or customer, and review 2 will be the other party of
  // the transaction.
  REVIEW_1_BY_PROVIDER: 'transition/review-1-by-provider',
  REVIEW_2_BY_PROVIDER: 'transition/review-2-by-provider',
  REVIEW_1_BY_CUSTOMER: 'transition/review-1-by-customer',
  REVIEW_2_BY_CUSTOMER: 'transition/review-2-by-customer',
  EXPIRE_CUSTOMER_REVIEW_PERIOD: 'transition/expire-customer-review-period',
  EXPIRE_PROVIDER_REVIEW_PERIOD: 'transition/expire-provider-review-period',
  EXPIRE_REVIEW_PERIOD: 'transition/expire-review-period',
};

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
export const states = {
  INITIAL: 'initial',
  INQUIRY: 'inquiry',
  OFFER_PENDING: 'offer-pending',
  CUSTOMER_OFFER_PENDING: 'customer-offer-pending',
  OFFER_ACCEPTED: 'offer-accepted',
  INQUIRY_CANCELED: 'inquiry-canceled',
  PENDING_PAYMENT: 'pending-payment',
  PAYMENT_EXPIRED: 'payment-expired',
  PREAUTHORIZED: 'preauthorized',
  DECLINED: 'declined',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
  DELIVERED: 'delivered',
  REVIEWED: 'reviewed',
  REVIEWED_BY_CUSTOMER: 'reviewed-by-customer',
  REVIEWED_BY_PROVIDER: 'reviewed-by-provider',
};

/**
 * The transitions that cancel a transaction before payment (see above), by the state they leave
 * and by who makes them. The graph and the action buttons are both built from this: a user gets a
 * cancel button only in a state that has a transition for their role.
 */
export const cancelBeforePaymentTransitions = {
  [states.INQUIRY]: {
    customer: transitions.CUSTOMER_CANCEL_FROM_INQUIRY,
    provider: transitions.PROVIDER_CANCEL_FROM_INQUIRY,
    operator: transitions.OPERATOR_CANCEL_FROM_INQUIRY,
    auto: transitions.AUTO_CANCEL_FROM_INQUIRY,
  },
  [states.OFFER_PENDING]: {
    customer: transitions.CUSTOMER_CANCEL_FROM_OFFER_PENDING,
    provider: transitions.PROVIDER_CANCEL_FROM_OFFER_PENDING,
    operator: transitions.OPERATOR_CANCEL_FROM_OFFER_PENDING,
    auto: transitions.AUTO_CANCEL_FROM_OFFER_PENDING,
  },
  [states.CUSTOMER_OFFER_PENDING]: {
    customer: transitions.CUSTOMER_CANCEL_FROM_CUSTOMER_OFFER_PENDING,
    provider: transitions.PROVIDER_CANCEL_FROM_CUSTOMER_OFFER_PENDING,
    operator: transitions.OPERATOR_CANCEL_FROM_CUSTOMER_OFFER_PENDING,
    auto: transitions.AUTO_CANCEL_FROM_CUSTOMER_OFFER_PENDING,
  },
  // Only the customer can cancel manually once the offer is accepted; the provider and the
  // operator cannot, only the backend's automatic cancellation applies to them
  [states.OFFER_ACCEPTED]: {
    customer: transitions.CUSTOMER_CANCEL_FROM_OFFER_ACCEPTED,
    auto: transitions.AUTO_CANCEL_FROM_OFFER_ACCEPTED,
  },
};

// The graph entries for leaving the given state by cancelling
const cancelOn = state =>
  Object.values(cancelBeforePaymentTransitions[state]).reduce(
    (on, transition) => ({ ...on, [transition]: states.INQUIRY_CANCELED }),
    {}
  );

/**
 * Description of transaction process graph
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const graph = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'default-booking/release-1',

  // This 'initial' state is a starting point for new transaction
  initial: states.INITIAL,

  // States
  states: {
    [states.INITIAL]: {
      on: {
        [transitions.INQUIRE]: states.INQUIRY,
        [transitions.REQUEST_PAYMENT]: states.PENDING_PAYMENT,
      },
    },
    [states.INQUIRY]: {
      on: {
        [transitions.MAKE_OFFER]: states.OFFER_PENDING,
        ...cancelOn(states.INQUIRY),
      },
    },

    [states.OFFER_PENDING]: {
      on: {
        [transitions.CUSTOMER_COUNTER_OFFER]: states.CUSTOMER_OFFER_PENDING,
        [transitions.CUSTOMER_ACCEPT_OFFER]: states.OFFER_ACCEPTED,
        ...cancelOn(states.OFFER_PENDING),
      },
    },

    [states.CUSTOMER_OFFER_PENDING]: {
      on: {
        [transitions.PROVIDER_COUNTER_OFFER]: states.OFFER_PENDING,
        [transitions.PROVIDER_ACCEPT_OFFER]: states.OFFER_ACCEPTED,
        ...cancelOn(states.CUSTOMER_OFFER_PENDING),
      },
    },

    [states.OFFER_ACCEPTED]: {
      on: {
        [transitions.REQUEST_PAYMENT_AFTER_INQUIRY]: states.PENDING_PAYMENT,
        ...cancelOn(states.OFFER_ACCEPTED),
      },
    },

    // Canceled before payment. The booking was declined, so the dates are available again.
    [states.INQUIRY_CANCELED]: {},

    [states.PENDING_PAYMENT]: {
      on: {
        [transitions.EXPIRE_PAYMENT]: states.PAYMENT_EXPIRED,
        [transitions.CONFIRM_PAYMENT]: states.PREAUTHORIZED,
      },
    },

    [states.PAYMENT_EXPIRED]: {},
    [states.PREAUTHORIZED]: {
      on: {
        [transitions.DECLINE]: states.DECLINED,
        [transitions.OPERATOR_DECLINE]: states.DECLINED,
        [transitions.EXPIRE]: states.EXPIRED,
        [transitions.ACCEPT]: states.ACCEPTED,
        [transitions.OPERATOR_ACCEPT]: states.ACCEPTED,
      },
    },

    [states.DECLINED]: {},
    [states.EXPIRED]: {},
    [states.ACCEPTED]: {
      on: {
        [transitions.CANCEL]: states.CANCELED,
        [transitions.COMPLETE]: states.DELIVERED,
        [transitions.OPERATOR_COMPLETE]: states.DELIVERED,
      },
    },

    [states.CANCELED]: {},
    [states.DELIVERED]: {
      on: {
        [transitions.EXPIRE_REVIEW_PERIOD]: states.REVIEWED,
        [transitions.REVIEW_1_BY_CUSTOMER]: states.REVIEWED_BY_CUSTOMER,
        [transitions.REVIEW_1_BY_PROVIDER]: states.REVIEWED_BY_PROVIDER,
      },
    },

    [states.REVIEWED_BY_CUSTOMER]: {
      on: {
        [transitions.REVIEW_2_BY_PROVIDER]: states.REVIEWED,
        [transitions.EXPIRE_PROVIDER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED_BY_PROVIDER]: {
      on: {
        [transitions.REVIEW_2_BY_CUSTOMER]: states.REVIEWED,
        [transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD]: states.REVIEWED,
      },
    },
    [states.REVIEWED]: { type: 'final' },
  },
};

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = transition => {
  return [
    transitions.ACCEPT,
    transitions.OPERATOR_ACCEPT,
    transitions.CANCEL,
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.CONFIRM_PAYMENT,
    transitions.DECLINE,
    transitions.OPERATOR_DECLINE,
    transitions.EXPIRE,
    transitions.MAKE_OFFER,
    transitions.CUSTOMER_COUNTER_OFFER,
    transitions.PROVIDER_COUNTER_OFFER,
    transitions.CUSTOMER_ACCEPT_OFFER,
    transitions.PROVIDER_ACCEPT_OFFER,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    ...Object.values(cancelBeforePaymentTransitions).flatMap(Object.values),
  ].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isCustomerReview = transition => {
  return [transitions.REVIEW_1_BY_CUSTOMER, transitions.REVIEW_2_BY_CUSTOMER].includes(transition);
};

// Processes might be different on how reviews are handled.
// Default processes use two-sided diamond shape, where either party can make the review first
export const isProviderReview = transition => {
  return [transitions.REVIEW_1_BY_PROVIDER, transitions.REVIEW_2_BY_PROVIDER].includes(transition);
};

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = transition => {
  return [
    transitions.REQUEST_PAYMENT,
    transitions.REQUEST_PAYMENT_AFTER_INQUIRY,
    transitions.MAKE_OFFER,
    transitions.CUSTOMER_COUNTER_OFFER,
    transitions.PROVIDER_COUNTER_OFFER,
  ].includes(transition);
};

// Check when transaction is completed (booking over)
export const isCompleted = transition => {
  const txCompletedTransitions = [
    transitions.COMPLETE,
    transitions.OPERATOR_COMPLETE,
    transitions.REVIEW_1_BY_CUSTOMER,
    transitions.REVIEW_1_BY_PROVIDER,
    transitions.REVIEW_2_BY_CUSTOMER,
    transitions.REVIEW_2_BY_PROVIDER,
    transitions.EXPIRE_REVIEW_PERIOD,
    transitions.EXPIRE_CUSTOMER_REVIEW_PERIOD,
    transitions.EXPIRE_PROVIDER_REVIEW_PERIOD,
  ];
  return txCompletedTransitions.includes(transition);
};

// Check when transaction is refunded (booking did not happen)
// In these transitions action/stripe-refund-payment is called
export const isRefunded = transition => {
  const txRefundedTransitions = [
    transitions.EXPIRE_PAYMENT,
    transitions.EXPIRE,
    transitions.CANCEL,
    transitions.DECLINE,
  ];
  return txRefundedTransitions.includes(transition);
};

export const statesNeedingProviderAttention = [states.PREAUTHORIZED];

// transitions that make the first offer
const makeOfferTransitions = ['transition/make-offer', 'transition/request-payment-after-inquiry'];

// transitions that update the offer
const updateOfferTransitions = ['transition/update-offer', 'transition/update-from-update-pending'];

// transitions that make a counter offer
const counterOfferTransitions = [
  'transition/provider-counter-offer',
  'transition/customer-counter-offer',
];

// transitions that revoke a counter offer
const revokeCounterOfferTransitions = [
  'transition/customer-withdraw-counter-offer',
  'transition/provider-reject-counter-offer',
];

// transitions that affect pricing on negotiation loop
const offerTransitions = [
  ...makeOfferTransitions,
  ...updateOfferTransitions,
  ...counterOfferTransitions,
  ...revokeCounterOfferTransitions,
];

const filterOfferTransitions = (transitions, offerTransitions) => {
  return transitions.filter(t => offerTransitions.includes(t.transition));
};

/**
 * Returns a new array of transitions with the offerInSubunits property added to the transitions that have a matching offer.
 *
 * @param {Array<TransitionRecord>} transitions
 * @param {Array<NegotiationOffer>} offers
 * @returns {Array<TransitionWithOfferInSubunitsRecord>}
 */
export const getTransitionsWithMatchingOffers = (transitions, offers) => {
  const isValidOffersArray = isValidNegotiationOffersArray(transitions, offers);
  if (isValidOffersArray) {
    const transitionsWithOffers = [];
    let offerIndex = 0;
    for (let i = 0; i < transitions.length; i++) {
      let transition = { ...transitions[i] };

      if (offerTransitions.includes(transition.transition)) {
        transition.offerInSubunits = offers[offerIndex]?.offerInSubunits;
        transitionsWithOffers.push(transition);
        offerIndex++;
      } else {
        transitionsWithOffers.push(transition);
      }
    }
    return transitionsWithOffers;
  }

  return transitions;
};

/**
 * Checks if the offers array is valid against the (past) transitions array.
 *
 * @param {Array<TransitionRecord>} transitions
 * @param {Array<NegotiationOffer>} offers
 * @returns {boolean}
 */
export const isValidNegotiationOffersArray = (transitions, offers) => {
  const pickedTransitions = filterOfferTransitions(transitions, offerTransitions);
  const isOffersAnArray = !!offers && Array.isArray(offers);

  // First check if we have the same number of offers and transitions
  if (!isOffersAnArray || offers.length !== pickedTransitions.length) {
    return false;
  }

  // Then verify that each offer corresponds to the transition at the same index
  // and that the order matches
  for (let i = 0; i < offers.length; i++) {
    const offer = offers[i];
    const transition = pickedTransitions[i];

    // Check if the offer's transition and actor match the transition at the same index
    if (offer.transition !== transition.transition || offer.by !== transition.by) {
      return false;
    }
  }

  return true;
};
